if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.classList.remove('bn-quick-add-modal--curated');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        if (!productElement) return;

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.curateProductInfo(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      curateProductInfo(productElement) {
        if (!productElement) return;

        const courseHandles = new Set([
          'curso-de-resina-epoxica',
          'curso-personalizado-de-resina-epoxica',
          'curso-de-resina-epoxica-personalizado',
          'curso-de-resina-epoxica-online',
          'curso-resina-pro',
          'maestria-en-resina-epoxica',
        ]);

        // Course products use purpose-built enrollment forms. Do not compress those
        // contracts into the standard physical-product quick view.
        if (courseHandles.has(productElement.dataset.productHandle)) {
          this.classList.remove('bn-quick-add-modal--curated');
          return;
        }

        const product = productElement.querySelector('.product');
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        const infoWrapper = productElement.querySelector('.product__info-wrapper');
        const info = productElement.querySelector('.product__info-container');
        if (!product || !mediaWrapper || !infoWrapper || !info) return;

        this.classList.add('bn-quick-add-modal--curated');
        productElement.classList.add('bn-quick-add-curated');
        product.classList.add('bn-quick-add-product');
        mediaWrapper.classList.add('bn-quick-add__media');
        infoWrapper.classList.add('bn-quick-add__info');

        // A compact photo carousel uses the product's complementary images without
        // importing the full PDP gallery UI. It sits directly under the principal image.
        this.buildQuickViewPhotoCarousel(mediaWrapper);

        // Reuse existing trust/payment content after the thumbnails instead of leaving
        // the media column visually empty while product controls grow on the right.
        const mediaExtras = document.createElement('div');
        mediaExtras.className = 'bn-quick-add__media-extras';

        ['.bn-pdp-payment-icons', '.icon-with-text'].forEach((selector) => {
          const node = info.querySelector(selector);
          if (node) mediaExtras.appendChild(node);
        });

        if (mediaExtras.childElementCount) mediaWrapper.appendChild(mediaExtras);

        const directChild = (node) => {
          let current = node;
          while (current && current.parentElement !== info) current = current.parentElement;
          return current && current.parentElement === info ? current : null;
        };

        const allowed = new Set();
        const allow = (selector) => {
          info.querySelectorAll(selector).forEach((node) => {
            const child = directChild(node);
            if (child) allowed.add(child);
          });
        };

        [
          '.product__title',
          '[id^="price-"]',
          '.volume-pricing-note',
          'variant-selects',
          'ks-product-inventory',
          '[id^="Quantity-Form-"]',
          '.savings-amount',
          '.pdp-buy-buttons-shell',
          '.btn--buy-on-whatsapp',
          '.product__view-details',
        ].forEach(allow);

        Array.from(info.children).forEach((child) => {
          if (!allowed.has(child)) child.remove();
        });
      }

      buildQuickViewPhotoCarousel(mediaWrapper) {
        if (!mediaWrapper || mediaWrapper.querySelector('.bn-quick-add__thumb-carousel')) return;

        const mediaItems = Array.from(mediaWrapper.querySelectorAll('.product__media-item'));
        const images = [];
        const seen = new Set();

        mediaItems.forEach((item) => {
          const image = item.querySelector('img');
          if (!image) return;
          const key = item.dataset.mediaId || image.currentSrc || image.getAttribute('src');
          if (!key || seen.has(key)) return;
          seen.add(key);
          images.push({ item, image, key });
        });

        if (images.length <= 1) return;

        const primaryEntry = images.find(({ item }) => item.classList.contains('is-active')) || images[0];
        const primaryImage = primaryEntry.image;
        const primaryContainer = primaryEntry.item.querySelector('.product-media-container');

        const carousel = document.createElement('div');
        carousel.className = 'bn-quick-add__thumb-carousel';
        carousel.setAttribute('aria-label', 'Imágenes adicionales del producto');

        const viewport = document.createElement('div');
        viewport.className = 'bn-quick-add__thumb-viewport';
        const track = document.createElement('div');
        track.className = 'bn-quick-add__thumb-track';
        viewport.appendChild(track);

        const buttons = [];
        const selectImage = (entry, button) => {
          const source = entry.image;
          ['src', 'srcset', 'alt', 'width', 'height'].forEach((attribute) => {
            const value = source.getAttribute(attribute);
            if (value) primaryImage.setAttribute(attribute, value);
            else if (attribute !== 'alt') primaryImage.removeAttribute(attribute);
          });

          const sourceContainer = entry.item.querySelector('.product-media-container');
          if (primaryContainer && sourceContainer) {
            ['--ratio', '--preview-ratio'].forEach((property) => {
              const value = sourceContainer.style.getPropertyValue(property);
              if (value) primaryContainer.style.setProperty(property, value);
            });
          }

          buttons.forEach((thumbButton) => {
            const active = thumbButton === button;
            thumbButton.classList.toggle('is-active', active);
            thumbButton.setAttribute('aria-current', active ? 'true' : 'false');
          });
        };

        const primaryIndex = Math.max(0, images.findIndex((entry) => entry.key === primaryEntry.key));

        images.forEach((entry, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'bn-quick-add__thumb';
          button.setAttribute('aria-label', `Ver imagen ${index + 1} de ${images.length}`);
          const isPrimary = index === primaryIndex;
          button.setAttribute('aria-current', isPrimary ? 'true' : 'false');
          if (isPrimary) button.classList.add('is-active');

          const thumb = entry.image.cloneNode(false);
          thumb.setAttribute('sizes', '72px');
          thumb.setAttribute('loading', 'lazy');
          thumb.setAttribute('decoding', 'async');
          thumb.alt = '';
          button.appendChild(thumb);
          button.addEventListener('click', () => selectImage(entry, button));
          buttons.push(button);
          track.appendChild(button);
        });

        carousel.appendChild(viewport);

        if (images.length > 4) {
          carousel.classList.add('bn-quick-add__thumb-carousel--with-nav');
          const prev = document.createElement('button');
          const next = document.createElement('button');
          prev.type = next.type = 'button';
          prev.className = 'bn-quick-add__thumb-nav bn-quick-add__thumb-nav--prev';
          next.className = 'bn-quick-add__thumb-nav bn-quick-add__thumb-nav--next';
          prev.setAttribute('aria-label', 'Imágenes anteriores');
          next.setAttribute('aria-label', 'Imágenes siguientes');
          prev.innerHTML = '<span aria-hidden="true">‹</span>';
          next.innerHTML = '<span aria-hidden="true">›</span>';
          prev.addEventListener('click', () => viewport.scrollBy({ left: -viewport.clientWidth * 0.8, behavior: 'smooth' }));
          next.addEventListener('click', () => viewport.scrollBy({ left: viewport.clientWidth * 0.8, behavior: 'smooth' }));
          carousel.prepend(prev);
          carousel.appendChild(next);
        }

        mediaWrapper.appendChild(carousel);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
